import React, { useEffect, useState } from "react";
import { Formik, useFormikContext } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import CustomCkeditor from "../../../common/customeditor";
import * as consentServices from "../../../services/consent.services";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../../utils/commonfunction";
import { SWAL_SETTINGS, INPUT_LENGTH_50, showFilterList } from "../../../../../utils/Constants";
import { Tooltip, Whisper, Loader } from "rsuite";
import { useTranslation } from "react-i18next";

const incrementVersion = (version) => {
  if (!version) return "v1";
  const regex = /\d+/g;
  const matches = [...version.matchAll(regex)];
  if (matches.length === 0) {
    return version + "1";
  }
  const lastMatch = matches[matches.length - 1];
  const index = lastMatch.index;
  const numStr = lastMatch[0];
  const incremented = String(Number(numStr) + 1);
  return version.substring(0, index) + incremented + version.substring(index + numStr.length);
};

const FormikWatcher = ({ showdefault, setFieldValue }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    if (showdefault && showdefault.description !== undefined) {
      const isDescriptionChanged = values.description !== (showdefault.description || "");
      if (isDescriptionChanged) {
        const nextVer = incrementVersion(showdefault.version || "v1");
        if (values.version !== nextVer) {
          setFieldValue("version", nextVer);
        }
      } else {
        if (values.version !== showdefault.version) {
          setFieldValue("version", showdefault.version || "");
        }
      }
    }
  }, [values.description, showdefault, setFieldValue]);

  return null;
};

const ConsentEdit = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [showdefault, setShowDefault] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const { t } = useTranslation();

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "User Consent Management", url: "/admin/cms/consent/list/1" },
    { title: t("btn_edit"), url: "" },
  ];

  useEffect(() => {
    consentServices
      .Details(params.id)
      .then((data) => {
        setShowDefault(data && data?.data ? data.data : {});
        setTimeout(() => {
          setDataLoaded(true);
        }, 100);
      })
      .catch((error) => {
        console.log("error=====>", error);
      });
  }, [params.id]);


  const typeOptions = [
    { value: "PARENT_LINK_CONSENT", label: "Parent Link Consent" },
    { value: "PARENT_LINK_CONSENT_TEEN", label: "Parent Link Consent Teen" },
    { value: "SELF_CONSENT_17", label: "Self Consent 17" },
     { value: "PRIVACY_POLICY", label: "Privacy Policy" },
        { value: "TERMS_CONDITIONS", label: "Terms Conditions" },
    { value: "CLUB_ADMIN_CONSENT", label: "Club Admin Consent" },
    { value: "PARENT_SELF_CONSENT", label: "Parent Self Consent" },
    { value: "DATA_PROTECTION", label: "Data Protection" },
    { value: "SAFE_GUARD_POLICY", label: "Safe Guard Policy" }
  ];

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded ? (
        <Formik
          enableReinitialize
          initialValues={{
            title: showdefault && showdefault.title ? showdefault.title : "",
            version: showdefault && showdefault.version ? showdefault.version : "",
            type: showdefault && showdefault.type ? showdefault.type : "",
            status: showdefault && showdefault.status ? showdefault.status : "ACTIVE",
            description: showdefault && showdefault.description ? showdefault.description : "",
          }}
          validate={(values) => {
            const error = {};
            const title = values.title?.trim() || "";
            const version = values.version?.trim() || "";
            const description = values.description;

            if (!title) error.title = "Title is required";
            else if (title.length > INPUT_LENGTH_50)
              error.title = t("validation_err_reached_maximum_length");

            if (!version) error.version = "Version is required";
            else if (version.length > 20)
              error.version = "Version should be less than 20 characters";

            if (!values.type) error.type = "Consent type is required";

            if (!values.status) error.status = t("validation_err_status_required");

            if (!description) error.description = "Consent description is required";

            return error;
          }}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            globalLoader(true);
            const id = params.id;
            const payload = {
              title: values.title,
              version: values.version,
              type: values.type,
              status: values.status,
              description: values.description,
            };

            consentServices
              .Edit(payload, id)
              .then((response) => {
                setSubmitting(false);
                if (response.success) {
                  Swal.fire({
                    icon: "success",
                    text: response.message || "Consent updated successfully",
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => {
                    resetForm({ values: "" });
                    globalLoader(false);
                    navigate(`/admin/cms/consent/list/1`);
                  }, 1000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  globalLoader(false);
                }
              })
              .catch((error) => {
                console.log("error ====> ", error);
                globalLoader(false);
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <FormikWatcher showdefault={showdefault} setFieldValue={setFieldValue} />
              <div className="row row-sm">
                <div className="col-lg-12 col-md-12 animation_fade">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div>
                        <h6 className="main-content-label mb-3">Edit User Consent Configuration</h6>
                      </div>
                      {showdefault && Object.keys(showdefault).length > 0 ? (
                        <div className="row row-sm">
                          <div className="col-lg-6 text-center form-group">
                            <label htmlFor="title" className="text-left d-flex">
                              Consent Title:<span className="requirestar">*</span>
                              <Whisper
                                placement="top"
                                controlId="control-id-hover"
                                trigger="hover"
                                speaker={
                                  <Tooltip>
                                    Enter a descriptive name for this user consent
                                  </Tooltip>
                                }
                              >
                                <span className="field-more-info mt-1 ms-1 cp">
                                  ?
                                </span>
                              </Whisper>
                            </label>
                            <input
                              name="title"
                              id="title"
                              type="text"
                              placeholder="Enter the title"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.title}
                              className="form-control"
                            />
                            <span className="text-danger d-flex text-left">
                              {errors.title && touched.title && errors.title}
                            </span>
                          </div>

                          <div className="col-lg-6 text-center form-group">
                            <label htmlFor="version" className="text-left d-flex">
                              Version (e.g. v1, v1.1):<span className="requirestar">*</span>
                            </label>
                            <input
                              name="version"
                              id="version"
                              type="text"
                              placeholder="Enter the version number"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.version}
                              className="form-control"
                            />
                            <span className="text-danger d-flex text-left">
                              {errors.version && touched.version && errors.version}
                            </span>
                          </div>

                          <div className="col-lg-6 text-center form-group">
                            <label htmlFor="type" className="text-left d-flex">
                              Consent Type:<span className="requirestar">*</span>
                            </label>
                            <div className="select-down-arrow">
                              <select
                                name="type"
                                id="type"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.type}
                                className="form-control"
                              >
                                <option value="">Select Consent Type</option>
                                {typeOptions.map((opt, i) => (
                                  <option key={i} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <span className="text-danger d-flex text-left">
                              {errors.type && touched.type && errors.type}
                            </span>
                          </div>

                          <div className="col-lg-6 text-center form-group">
                            <label htmlFor="status" className="text-left d-flex">
                              {t("list_heading_status")}:<span className="requirestar">*</span>
                            </label>
                            <div className="select-down-arrow">
                              <select
                                name="status"
                                id="status"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.status}
                                className="form-control"
                              >
                                {showFilterList.map((value, i) => (
                                  <option key={i} value={value.status__id}>
                                    {t(`${value.name}`)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <span className="text-danger d-flex text-left">
                              {errors.status && touched.status && errors.status}
                            </span>
                          </div>

                          <div className="col-lg-12 text-center form-group">
                            <label htmlFor="description" className="text-left d-flex">
                              Consent Text / Policy Details:<span className="requirestar">*</span>
                            </label>
                            <CustomCkeditor
                              fieldname={"description"}
                              setFieldValue={setFieldValue}
                              value={values.description}
                              setFieldTouched={setFieldTouched}
                            />
                            <span className="text-danger d-flex text-left">
                              {errors.description && touched.description && errors.description}
                            </span>
                          </div>

                          <div className="mt-3">
                            <button
                              className="btn btn-main-primary signbtn mr-2"
                              type="submit"
                            >
                              <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                              {t("btn_submit")}
                            </button>
                            <button
                              className="btn ripple btn-secondary"
                              type="button"
                              onClick={() => navigate(-1)}
                            >
                              <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                              {t("btn_cancel")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <Loader />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default ConsentEdit;
